import {Button} from "antd";
import {ApolloError} from "@apollo/client";
import {FormikProps} from "formik";
import React, {FC, memo} from "react";
import {InfoCircleTwoTone, SaveOutlined} from "@ant-design/icons";
import classnames from "classnames";
import {CourseAdditionalItemInput, EditCourseInput, Maybe, MonthlySchedule, NewCourseInput, useGetAppSettingsQuery, useGetCompanyInfoQuery} from "../../generated/graphql";
import i18n from "../../services/i18n";
import LayoutStep from "../../views/LayoutStep/LayoutStep";
import {CourseFeeInput, CourseTaxInput, CoursePaymentCheckbox, IncludedEntranceFeeInput, CourseDiscountInput} from "./CourseViews/CourseStepViews";
import MollieCoursePanel from "./CourseViews/MollieCoursePanel";
import AdditionalItemsEditor from "./CourseViews/AdditionalItemsEditor";
import CoursePricePreview from "./CourseCreate/CoursePricePreview";
import CourseLateEntryPricingSettings from "./CourseViews/CourseLateEntryPricingSettings";
import {CourseLateEntryPricingFormFields} from "./courseLateEntryPricing";
import css from "./StepCoursePrice.less";

type PossibleFormikPropsType = NewCourseInput | EditCourseInput;

type StepCoursePriceProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    error: ApolloError | Error | undefined;
    courseNumber?: string;
    onSave: () => void;
    subtitle?: Maybe<string> | React.ReactNode;
    style?: React.CSSProperties;
    loading?: boolean; // Add this prop
};

const StepCoursePrice: FC<StepCoursePriceProps> = ({
    formikProps,
    error,
    courseNumber,
    onSave,
    subtitle,
    style,
    loading, // Add this prop
}) => {
    // CHECKING IF ONLY ESR IS AVAILABLE ----------------------------------------------------------------------
    const [onlyEsr, setOnlyEsr] = React.useState<boolean>(false);
    const [esrActive, setEsrActive] = React.useState<boolean>(false);
    const {data: appSettingsData} = useGetAppSettingsQuery();

    React.useEffect(() => {
        const settings = appSettingsData?.settings;

        if (settings) {
            const {esr, cash, bankTransfer, sepaDebit} = settings;

            if (esr) setEsrActive(true);

            if (esr && !cash && !bankTransfer && !sepaDebit) {
                setOnlyEsr(true);
            }
        }
    }, [appSettingsData]);

    // Phase 22-04 — Mollie-Card-Gating (D-07/D-08, BookerPaymentMethodsStep-Pattern).
    // Strict === true (kein Default-true, kein Fallback) — Tenants ohne featureMollie
    // sehen die Kurspreis-UI byte-identisch zu heute (R-5).
    const featureMollie = appSettingsData?.settings?.featureMollie === true;

    // Zusatzposten: nur mit Tenant-Flag und nicht bei Einzelbuchungs-Kursen
    // (V1-Einschränkung, Server lehnt Posten auf allowLessonBooking-Kursen ab).
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureAdditionalItems = companyInfoData?.companyInfo?.subscription?.featureAdditionalItems === true;
    const showAdditionalItems = featureAdditionalItems && !formikProps.values.allowLessonBooking;

    return (
        <LayoutStep
            columns={16}
            title={i18n.containers.courses.CourseCreate.courseStepCoursePrice.title()}
            subtitle={subtitle}
            button={
                <Button type="primary" onClick={onSave} icon={<SaveOutlined />} loading={loading} disabled={loading}>
                    {i18n.containers.courses.CourseTypes.buttons.save()}
                </Button>
            }
            error={error}
            style={style}
            lessonBooking={Boolean(formikProps.values.allowLessonBooking)}
        >
            <div className={css.leftSide} style={{gridColumn: "1 / span 8"}}>
                <h4 className={classnames(css.headerInFlow)}>{"Einstellungen"}</h4>
                <CourseFeeInput
                    label={i18n.containers.courses.CourseCreate.courseFee.label()}
                    formikProps={formikProps}
                    testId="CourseFeeInput"
                    style={{gridColumn: "1 / span 12"}}
                    disabled={Boolean(formikProps.values.allowMonthlyPayment && formikProps.values.monthlySchedule === MonthlySchedule.Custom)}
                />
                <CourseTaxInput
                    label={i18n.containers.courses.CourseCreate.courseTax.label()}
                    placeholder={i18n.containers.courses.CourseCreate.courseTax.taxes.nineteen()}
                    formikProps={formikProps}
                    testId="CourseTaxInput"
                    style={{gridColumn: "1 / span 6"}}
                />
                <CourseDiscountInput
                    label={i18n.containers.courses.CourseCreate.courseDiscount.label()}
                    placeholder={i18n.containers.courses.CourseCreate.courseDiscount.placeholder()}
                    formikProps={formikProps}
                    testId="CourseDiscountInput"
                    style={{gridColumn: "7 / span 6"}}
                />
                {featureMollie && (
                    <MollieCoursePanel
                        formikProps={formikProps}
                        style={{gridColumn: "1 / span 12"}}
                        enabledMollieMethods={appSettingsData?.settings?.enabledMollieMethods}
                    />
                )}
                <CoursePaymentCheckbox
                    label={i18n.containers.courses.CourseCreate.paymentOptions.label()}
                    formikProps={formikProps}
                    testId="CoursePaymentCheckbox"
                    style={{gridColumn: "1 / span 12"}}
                    onlyEsrIsAvailable={onlyEsr}
                />
                <IncludedEntranceFeeInput
                    label={i18n.containers.courses.CourseCreate.includedEntranceFee.label()}
                    placeholder={i18n.containers.courses.CourseCreate.includedEntranceFee.placeholder()}
                    formikProps={formikProps}
                    testId="IncludedEntranceFeeInput"
                    style={{gridColumn: "1 / span 12"}}
                />
            </div>
            <div className={css.rightSide} style={{gridColumn: "9 / span 8"}}>
                <div className={css.rightSide} style={{gridColumn: "9 / span 8"}}>
                    <h4 className={classnames(css.header, css.rightSide)}>{"Vorschau für die Abrechnung"}</h4>
                    <p className={css.headerDescription}>
                        <InfoCircleTwoTone />
                        {"Hier wird dargestellt, wie die Abrechnungen für Buchungen des Kurses aussehen werden."}
                    </p>
                </div>

                <CoursePricePreview formikProps={formikProps} style={{gridColumn: "9 / span 12"}} esrActive={esrActive} />
                {showAdditionalItems && (
                    <AdditionalItemsEditor
                        value={(formikProps.values.additionalItems ?? []) as Array<CourseAdditionalItemInput>}
                        onChange={(items) => formikProps.setFieldValue("additionalItems", items)}
                        testId="AdditionalItemsEditor"
                        style={{gridColumn: "9 / span 12", marginTop: 16}}
                    />
                )}
                <CourseLateEntryPricingSettings
                    formikProps={
                        formikProps as unknown as FormikProps<CourseLateEntryPricingFormFields>
                    }
                />
            </div>
        </LayoutStep>
    );
};

export default memo(StepCoursePrice);
